'use client'

import { Suspense, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { createClient } from '@repo/db/browser'
import { Button, Input, Label } from '@repo/ui'
import { ACCESS_DENIAL_MESSAGES, isAccessDenial, parseIp } from '@/lib/access-policy'
import { signInToPanel } from './actions'

/**
 * Operatör oturum başlangıcını kaydeder (best-effort). Giriş kısıtı (gün/saat, IP)
 * ihlalinde middleware bu ilk isteği 403 + gerekçeyle reddedip oturumu kapatır;
 * o durumda kullanıcıya gösterilecek mesaj döner.
 */
async function startPanelSession(): Promise<string | null> {
  try {
    const res = await fetch('/api/admin/heartbeat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({ event: 'login' }),
    })
    if (res.status !== 403) return null
    const body = await res.json().catch(() => null) as { reason?: unknown; ip?: unknown } | null
    if (!isAccessDenial(body?.reason)) return null
    const ip = typeof body.ip === 'string' && parseIp(body.ip) ? body.ip : null
    return ACCESS_DENIAL_MESSAGES[body.reason] + (body.reason === 'erisim-ip' && ip ? ` (IP adresiniz: ${ip})` : '')
  } catch {
    return null
  }
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginFallback />}>
      <LoginInner />
    </Suspense>
  )
}

function LoginFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8 border rounded-xl shadow-sm">
        <h1 className="text-2xl font-bold text-center">Dashboard Giriş</h1>
        <div className="h-40" />
      </div>
    </div>
  )
}

function LoginInner() {
  const searchParams = useSearchParams()
  const step = searchParams.get('step')
  const prefillEmail = searchParams.get('email') ?? ''
  const idleReason = searchParams.get('reason') === 'idle'
  const passwordResetDone = searchParams.get('reason') === 'sifre-yenilendi'
  const reasonParam = searchParams.get('reason')
  const accessDenial = isAccessDenial(reasonParam) ? reasonParam : null
  const deniedIp = searchParams.get('ip') ?? ''
  const [email, setEmail] = useState(prefillEmail)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [mfaStep, setMfaStep] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [factorId, setFactorId] = useState('')
  // Marketing'den gelen email varsa kilitli modda göster (read-only),
  // kullanıcı "Farklı e-posta" derse normal form'a geçer.
  const [emailLocked, setEmailLocked] = useState(Boolean(prefillEmail))
  const passwordRef = useRef<HTMLInputElement | null>(null)

  // MFA adımında oturumun doğrulama yöntemini yükle. Normal giriş sunucu
  // action'ı ile yapılır; burada eşzamanlı signOut yeni çerezi silebilir.
  useEffect(() => {
    const supabase = createClient()
    if (step === 'mfa') {
      supabase.auth.mfa.listFactors().then(({ data, error }) => {
        const factor = data?.totp.find(f => f.status === 'verified')
        if (error || !factor) setError('Doğrulama yöntemi yüklenemedi. Yeniden giriş yapın.')
        else { setFactorId(factor.id); setMfaStep(true) }
      })
    }
  }, [step])

  // Email query param geldiyse focus'u şifre alanına taşı
  useEffect(() => {
    if (prefillEmail) {
      passwordRef.current?.focus()
    }
  }, [prefillEmail])

  const forgotPasswordHref = email.trim()
    ? `/login/sifremi-unuttum?email=${encodeURIComponent(email.trim())}`
    : '/login/sifremi-unuttum'
  const passwordResetNotice = passwordResetDone && (
    <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
      Şifreniz güncellendi. Yeni şifrenizle giriş yapın.
    </div>
  )
  // Middleware giriş kısıtı nedeniyle oturumu kapattığında (gün/saat dışı veya izinsiz ağ).
  const accessDeniedNotice = accessDenial && (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200">
      {ACCESS_DENIAL_MESSAGES[accessDenial]}
      {accessDenial === 'erisim-ip' && parseIp(deniedIp) !== null && (
        <> IP adresiniz: <span className="font-mono">{deniedIp}</span></>
      )}
    </div>
  )

  function handleUseDifferentEmail() {
    setEmailLocked(false)
    setEmail('')
    setPassword('')
    setError('')
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    let result: Awaited<ReturnType<typeof signInToPanel>>
    try {
      result = await signInToPanel(email, password)
    } catch {
      setError('Sayfayı yenileyip tekrar deneyin.')
      setLoading(false)
      return
    }
    if (!result.success) { setError(result.error); setLoading(false); return }

    if (result.mfa) {
      window.location.href = '/login?step=mfa'
      return
    }

    // Operatör oturum başlangıcını kaydet (best-effort); giriş kısıtı varsa yönlendirme yerine gerekçe.
    const accessError = await startPanelSession()
    if (accessError) {
      await createClient().auth.signOut({ scope: 'local' })
      setError(accessError)
      setLoading(false)
      return
    }

    // Full page load ile yönlendir — router.push + router.refresh, session
    // expire sonrası client-side cache nedeniyle takılabiliyor.
    window.location.href = '/'
  }

  async function handleMfaVerify(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    const supabase = createClient()
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
    if (challengeError) {
      setError(challengeError.message)
      setLoading(false)
      return
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: totpCode,
    })

    if (verifyError) {
      setError('Doğrulama kodu hatalı. Tekrar deneyin.')
      setLoading(false)
      return
    }

    // Operatör oturum başlangıcını kaydet (best-effort); giriş kısıtı varsa giriş formuna gerekçeyle dön.
    const accessError = await startPanelSession()
    if (accessError) {
      await supabase.auth.signOut({ scope: 'local' })
      setMfaStep(false)
      setTotpCode('')
      setError(accessError)
      setLoading(false)
      return
    }

    window.location.href = '/'
  }

  // Sunucu action'ı oturum çerezini yazınca bu sayfa oturumlu layout'la yeniden
  // çizilir; tam ekran katman yönlendirme bitene kadar kenar çubuğunu gizler.
  // Hata olursa loading kapanır ve form, girilen değerleriyle geri gelir.
  if (loading && !mfaStep) {
    return (
      <div role="status" aria-live="polite" className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-background">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <p className="text-sm text-muted-foreground">Giriş yapılıyor…</p>
      </div>
    )
  }

  if (mfaStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-sm space-y-6 p-8 border rounded-xl shadow-sm">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">İki Faktörlü Doğrulama</h1>
            <p className="text-sm text-muted-foreground">
              Authenticator uygulamanızdaki 6 haneli kodu girin
            </p>
          </div>
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="totpCode">Doğrulama Kodu</Label>
              <Input
                id="totpCode"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading || totpCode.length !== 6}>
              {loading ? 'Doğrulanıyor...' : 'Doğrula'}
            </Button>
          </form>
        </div>
      </div>
    )
  }

  if (emailLocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-sm space-y-6 p-8 border rounded-xl shadow-sm">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">Dashboard Giriş</h1>
            <p className="text-sm text-muted-foreground">Şifrenizi girerek devam edin</p>
          </div>
          {accessDeniedNotice}
          {passwordResetNotice}

          <div className="rounded-lg border bg-muted/40 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">E-posta</p>
            <p className="text-sm font-medium truncate">{email}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                ref={passwordRef}
                type="password"
                placeholder="Şifre"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>

          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={handleUseDifferentEmail}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Farklı e-posta ile giriş yap
            </button>
            <Link href={forgotPasswordHref} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
              Şifremi unuttum
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8 border rounded-xl shadow-sm">
        <h1 className="text-2xl font-bold text-center">Dashboard Giriş</h1>
        {idleReason && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
            Uzun süre işlem yapılmadığı için oturumunuz sonlandırıldı.
          </div>
        )}
        {accessDeniedNotice}
        {passwordResetNotice}
        <form onSubmit={handleLogin} className="space-y-4">
          <Input
            type="email"
            placeholder="E-posta"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            ref={passwordRef}
            type="password"
            placeholder="Şifre"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </Button>
        </form>
        <div className="text-center">
          <Link href={forgotPasswordHref} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
            Şifremi unuttum
          </Link>
        </div>
      </div>
    </div>
  )
}
